import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,nofollow');
    setMobileOpen(false);
  };

  const articleSlugs = [
    { label: 'Complete Guide 2026', slug: '/article/pandabuy-spreadsheet-guide' },
    { label: 'Best Sneakers Spreadsheet', slug: '/article/best-pandabuy-sneakers-spreadsheet' },
    { label: 'Clothing Spreadsheet Tips', slug: '/article/pandabuy-clothing-spreadsheet-tips' },
    { label: 'Budget Finds Under $30', slug: '/article/budget-pandabuy-spreadsheet-finds' },
    { label: 'How to Use Tutorial', slug: '/article/how-to-use-pandabuy-spreadsheet' },
    { label: 'Spreadsheet vs Manual', slug: '/article/pandabuy-spreadsheet-vs-manual-browsing' },
    { label: 'Top 10 Categories', slug: '/article/top-pandabuy-spreadsheet-categories' },
    { label: 'Community Trends 2026', slug: '/article/pandabuy-spreadsheet-community-trends' },
    { label: 'Safety & Scam Tips', slug: '/article/pandabuy-spreadsheet-safety-tips' },
    { label: 'FAQ – 20 Questions', slug: '/article/pandabuy-spreadsheet-faq' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b ${
          scrolled ? 'border-background-200/70 shadow-sm' : 'border-background-200/40'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-950 hover:text-primary-600 transition-colors duration-200"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-file-list-3-line text-lg text-background-50"></i>
              </div>
              <span className="text-lg font-semibold font-label whitespace-nowrap">
                Pandabuy<span className="text-primary-500">Sheet</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line mr-1.5"></i>Home
              </Link>

              <div className="relative" ref={guideRef}>
                <button
                  onClick={() => setGuideOpen(!guideOpen)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium font-label text-foreground-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line mr-1"></i>Shopping Guide
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {guideOpen && (
                  <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-50 py-2 max-h-80 overflow-y-auto">
                    <p className="px-4 py-2 text-xs font-semibold font-label text-foreground-400 uppercase tracking-wider">
                      Pandabuy Sheet Guides
                    </p>
                    {articleSlugs.map((a) => (
                      <Link
                        key={a.slug}
                        to={a.slug}
                        onClick={() => setGuideOpen(false)}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
                      >
                        {a.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 text-sm font-medium font-label bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Pandabuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 text-sm font-medium font-label bg-foreground-950 text-background-50 rounded-lg hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex md:hidden items-center justify-center w-10 h-10 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl text-foreground-800 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-4 py-4 space-y-3">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 bg-background-100 rounded-xl text-sm font-semibold font-label text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
              >
                <i className="ri-home-line text-lg"></i> Home
              </Link>

              <div className="pt-2">
                <p className="px-4 py-2 text-xs font-semibold font-label text-foreground-400 uppercase tracking-wider">
                  Shopping Guides
                </p>
                {articleSlugs.map((a) => (
                  <Link
                    key={a.slug}
                    to={a.slug}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors duration-150 cursor-pointer rounded-lg"
                  >
                    <i className="ri-article-line text-base text-foreground-400"></i> {a.label}
                  </Link>
                ))}
              </div>

              <div className="pt-2 border-t border-background-200/70">
                <p className="px-4 py-2 text-xs font-semibold font-label text-foreground-400 uppercase tracking-wider">
                  Categories
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="flex items-center gap-2 px-3 py-2.5 bg-background-100 rounded-xl text-xs font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 cursor-pointer text-left"
                    >
                      <span className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${cat.colorClass}`}>
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-3 border-t border-background-200/70">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full px-5 py-3 text-sm font-semibold font-label bg-primary-500 text-background-50 rounded-xl hover:bg-primary-600 transition-all duration-200 text-center whitespace-nowrap cursor-pointer"
                >
                  Pandabuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full px-5 py-3 text-sm font-semibold font-label bg-foreground-950 text-background-50 rounded-xl hover:bg-foreground-800 transition-all duration-200 text-center whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}